"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Link from "next/link";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import BrainMobileAbout from "./BrainMobileAbout";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ApiWave } from "@/generated/models/ApiWave";
import CommunityCurations from "@/components/community-curations/CommunityCurations";
import MyStreamWaveLeaderboard from "../my-stream/MyStreamWaveLeaderboard";
import MyStreamWaveSubmissions from "../my-stream/MyStreamWaveSubmissions";
import MyStreamWaveOutcome from "../my-stream/MyStreamWaveOutcome";
import MyStreamWaveSales from "../my-stream/MyStreamWaveSales";
import MyStreamWavePolls from "../my-stream/MyStreamWavePolls";
import MyStreamWaveMyVotes from "../my-stream/votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "../my-stream/MyStreamWaveFAQ";
import BrainMobileWaves from "./BrainMobileWaves";
import BrainMobileMessages from "./BrainMobileMessages";
import BrainNotifications from "../notifications/NotificationsContainer";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { BrainView } from "./brainMobileViews";
import { useLayout } from "../my-stream/layout/LayoutContext";
interface BrainMobileViewContentProps {
  readonly activeView: BrainView;
  readonly activeWaveId: string | null;
  readonly children: ReactNode;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly isApproveWave?: boolean | undefined;
  readonly hasPolls?: boolean | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly onOpenQuickVote: () => void;
  readonly onPrefetchQuickVote?: (() => void) | undefined;
  readonly wave: ApiWave | null | undefined;
}
function BrainMobileProfileFeed() {
  const $ = _c(3);
  const {
    mobileWavesViewStyle
  } = useLayout();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-mb-5"><Link href="/waves" prefetch={false} className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-300 tw-no-underline tw-transition desktop-hover:hover:tw-border-iron-600 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-white"><ArrowLeftIcon className="tw-size-4" aria-hidden="true" /><span>Back to Waves</span></Link></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== mobileWavesViewStyle) {
    t1 = <CommunityCurations heightStyle={mobileWavesViewStyle} topContent={t0} />;
    $[1] = mobileWavesViewStyle;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export default function BrainMobileViewContent(t0) {
  const $ = _c(44);
  const {
    activeView,
    activeWaveId,
    children,
    isCurationWave,
    isMemesWave,
    isRankWave,
    isApproveWave: t1,
    hasPolls: t2,
    onDropClick,
    onOpenQuickVote,
    onPrefetchQuickVote,
    wave
  } = t0;
  const isApproveWave = t1 === undefined ? false : t1;
  const hasPolls = t2 === undefined ? false : t2;
  const isCompetitionWave = isRankWave || isApproveWave;
  const supportsOutcomeView = isCompetitionWave && !isCurationWave;
  const rankWave = isCompetitionWave ? wave ?? null : null;
  const outcomeWave = supportsOutcomeView ? wave ?? null : null;
  const curationWave = isCurationWave ? wave ?? null : null;
  const faqWave = isMemesWave ? wave ?? null : null;
  let t3;
  if ($[0] !== onDropClick || $[1] !== rankWave) {
    t3 = rankWave ? <MyStreamWaveLeaderboard key={rankWave.id} wave={rankWave} onDropClick={onDropClick} /> : null;
    $[0] = onDropClick;
    $[1] = rankWave;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const leaderboardContent = t3;
  let t4;
  if ($[3] !== onDropClick || $[4] !== rankWave) {
    t4 = rankWave ? <MyStreamWaveSubmissions key={rankWave.id} wave={rankWave} onDropClick={onDropClick} /> : null;
    $[3] = onDropClick;
    $[4] = rankWave;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const submissionsContent = t4;
  let t5;
  if ($[6] !== curationWave) {
    t5 = curationWave ? <MyStreamWaveSales waveId={curationWave.id} /> : null;
    $[6] = curationWave;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const salesContent = t5;
  let t6;
  if ($[8] !== onDropClick || $[9] !== rankWave) {
    t6 = rankWave ? <div className="tw-px-2 sm:tw-px-4"><WaveWinners wave={rankWave} onDropClick={onDropClick} /></div> : null;
    $[8] = onDropClick;
    $[9] = rankWave;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const winnersContent = t6;
  let t7;
  if ($[11] !== outcomeWave) {
    t7 = outcomeWave ? <MyStreamWaveOutcome wave={outcomeWave} /> : null;
    $[11] = outcomeWave;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const outcomeContent = t7;
  let t8;
  if ($[13] !== onDropClick || $[14] !== rankWave) {
    t8 = rankWave ? <MyStreamWaveMyVotes wave={rankWave} onDropClick={onDropClick} /> : null;
    $[13] = onDropClick;
    $[14] = rankWave;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const myVotesContent = t8;
  let t9;
  if ($[16] !== hasPolls || $[17] !== onDropClick || $[18] !== wave) {
    t9 = wave && hasPolls ? <MyStreamWavePolls wave={wave} onDropClick={onDropClick} /> : null;
    $[16] = hasPolls;
    $[17] = onDropClick;
    $[18] = wave;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const pollsContent = t9;
  let t10;
  if ($[20] !== faqWave) {
    t10 = faqWave ? <MyStreamWaveFAQ wave={faqWave} /> : null;
    $[20] = faqWave;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const faqContent = t10;
  let t11;
  if ($[22] !== children) {
    t11 = <>{children}</>;
    $[22] = children;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== activeWaveId) {
    t12 = <BrainMobileAbout activeWaveId={activeWaveId} />;
    $[24] = activeWaveId;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== onOpenQuickVote || $[27] !== onPrefetchQuickVote) {
    t13 = <BrainMobileWaves onOpenQuickVote={onOpenQuickVote} onPrefetchQuickVote={onPrefetchQuickVote} />;
    $[26] = onOpenQuickVote;
    $[27] = onPrefetchQuickVote;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BrainMobileProfileFeed />;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <BrainMobileMessages />;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <BrainNotifications />;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== faqContent || $[33] !== leaderboardContent || $[34] !== myVotesContent || $[35] !== outcomeContent || $[36] !== pollsContent || $[37] !== salesContent || $[38] !== submissionsContent || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== winnersContent) {
    t17 = {
      [BrainView.DEFAULT]: t11,
      [BrainView.ABOUT]: t12,
      [BrainView.LEADERBOARD]: leaderboardContent,
      [BrainView.SUBMISSIONS]: submissionsContent,
      [BrainView.SALES]: salesContent,
      [BrainView.WINNERS]: winnersContent,
      [BrainView.OUTCOME]: outcomeContent,
      [BrainView.MY_VOTES]: myVotesContent,
      [BrainView.POLLS]: pollsContent,
      [BrainView.FAQ]: faqContent,
      [BrainView.WAVES]: t13,
      [BrainView.PROFILE_FEED]: t14,
      [BrainView.MESSAGES]: t15,
      [BrainView.NOTIFICATIONS]: t16
    };
    $[32] = faqContent;
    $[33] = leaderboardContent;
    $[34] = myVotesContent;
    $[35] = outcomeContent;
    $[36] = pollsContent;
    $[37] = salesContent;
    $[38] = submissionsContent;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = winnersContent;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const contentByView = t17;
  return contentByView[activeView];
}